import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  EyeOffIcon,
  ClipboardCheckIcon,
  AdjustmentsIcon,
  BriefcaseIcon,
  LightningBoltIcon,
  ChartBarIcon,
  BadgeCheckIcon,
  LocationMarkerIcon,
  TrendingUpIcon,
  CubeIcon,
  StarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/outline';
import { businessMetrics, formatters } from '../../config/businessMetrics';

export default function ProcessSection() {
  const [activePhase, setActivePhase] = useState(0);
  const [lastInteraction, setLastInteraction] = useState(Date.now());

  // Add CSS keyframes for rotation animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes orbit-premium-0 {
        from { transform: translate(-50%, -50%) rotate(0deg) translateX(175px) rotate(0deg); }
        to { transform: translate(-50%, -50%) rotate(360deg) translateX(175px) rotate(-360deg); }
      }
      @keyframes orbit-premium-1 {
        from { transform: translate(-50%, -50%) rotate(72deg) translateX(175px) rotate(-72deg); }
        to { transform: translate(-50%, -50%) rotate(432deg) translateX(175px) rotate(-432deg); }
      }
      @keyframes orbit-premium-2 {
        from { transform: translate(-50%, -50%) rotate(144deg) translateX(175px) rotate(-144deg); }
        to { transform: translate(-50%, -50%) rotate(504deg) translateX(175px) rotate(-504deg); }
      }
      @keyframes orbit-premium-3 {
        from { transform: translate(-50%, -50%) rotate(216deg) translateX(175px) rotate(-216deg); }
        to { transform: translate(-50%, -50%) rotate(576deg) translateX(175px) rotate(-576deg); }
      }
      @keyframes orbit-premium-4 {
        from { transform: translate(-50%, -50%) rotate(288deg) translateX(175px) rotate(-288deg); }
        to { transform: translate(-50%, -50%) rotate(648deg) translateX(175px) rotate(-648deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const [isVisible, setIsVisible] = useState({});
  const hoveredCard = null; // legacy stub to guard against stale hot-reload references
  const observerRef = useRef(null);

  // Intersection Observer for animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.observe-me');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  // Auto-rotate through phases with extended delay after manual interaction
  useEffect(() => {
    const interval = setInterval(() => {
      const timeSinceInteraction = Date.now() - lastInteraction;
      // Wait 30 seconds after manual interaction before auto-rotating
      if (timeSinceInteraction >= 30000) {
        setActivePhase((prev) => (prev + 1) % 4);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [lastInteraction]);

  // Handler for manual phase selection
  const handlePhaseClick = (index) => {
    setActivePhase(index);
    setLastInteraction(Date.now()); // Reset timer on manual click
  };

  const auctionPhases = [
    {
      id: 0,
      phase: 'Phase 1',
      title: 'Strategic Positioning',
      weeks: 'Weeks 1-4',
      icon: LocationMarkerIcon,
      iconEmoji: '🎯',
      color: 'from-blue-500 to-indigo-600',
      glowColor: 'from-blue-500/20 via-indigo-500/20 to-indigo-600/20',
      borderColor: 'border-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      ringColor: 'ring-blue-500/30',
      description: 'Transform your business into an acquisition target that commands premium valuations',
      activities: [
        'Comprehensive business analysis & value driver identification',
        'Creation of institutional-grade marketing materials (CIM)',
        'Strategic positioning for maximum appeal',
        'Preliminary valuation modeling & market analysis',
      ],
      outcome: 'Investment-ready business profile',
      stat: '2.3x',
      statLabel: 'Average valuation improvement',
    },
    {
      id: 1,
      phase: 'Phase 2',
      title: 'Market Creation',
      weeks: 'Weeks 5-8',
      icon: TrendingUpIcon,
      iconEmoji: '🚀',
      color: 'from-purple-500 to-pink-600',
      glowColor: 'from-purple-500/20 via-fuchsia-500/20 to-pink-600/20',
      borderColor: 'border-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      ringColor: 'ring-purple-500/30',
      description: 'Launch a controlled auction process to create competitive dynamics',
      activities: [
        'Confidential outreach to 200,000 qualified buyers',
        'NDA execution and information distribution',
        'Management presentations and Q&A sessions',
        'First round bid solicitation',
      ],
      outcome: 'Multiple competing buyers',
      stat: '12-15',
      statLabel: 'Average interested parties',
    },
    {
      id: 2,
      phase: 'Phase 3',
      title: 'Value Maximization',
      weeks: 'Weeks 9-12',
      icon: CubeIcon,
      iconEmoji: '💎',
      color: 'from-emerald-500 to-teal-600',
      glowColor: 'from-emerald-500/20 via-teal-500/20 to-teal-600/20',
      borderColor: 'border-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      ringColor: 'ring-emerald-500/30',
      description: 'Leverage competitive tension to drive optimal terms and valuations',
      activities: [
        'LOI negotiation with top bidders',
        'Structured bidding process management',
        'Deal structure optimization',
        'Final buyer selection',
      ],
      outcome: 'Best possible deal terms',
      stat: '35%',
      statLabel: 'Average increase from first offer',
    },
    {
      id: 3,
      phase: 'Phase 4',
      title: 'Flawless Execution',
      weeks: 'Weeks 13-24',
      icon: StarIcon,
      iconEmoji: '✨',
      color: 'from-amber-500 to-orange-600',
      glowColor: 'from-amber-500/20 via-orange-500/20 to-orange-600/20',
      borderColor: 'border-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      ringColor: 'ring-amber-500/30',
      description: 'Navigate due diligence to successful close with certainty',
      activities: [
        'Due diligence management & data room',
        'Purchase agreement negotiation',
        'Regulatory and third-party approvals',
        'Closing coordination & funds transfer',
      ],
      outcome: 'Successful exit achieved',
      stat: '95%',
      statLabel: 'Close rate after LOI',
    },
  ];

  const processMetrics = [
    {
      metric: '$2.5M+',
      label: 'Average Additional Value Created',
      description: 'Through competitive auction dynamics',
      icon: '📈',
      gradient: 'from-blue-400 to-blue-600',
    },
    {
      metric: '3.5',
      label: 'Average Competing Offers',
      description: 'Creating negotiating leverage',
      icon: '🏆',
      gradient: 'from-gray-700 to-gray-900',
    },
    {
      metric: '6 Months',
      label: 'Typical Transaction Timeline',
      description: 'From engagement to closing',
      icon: '⚡',
      gradient: 'from-emerald-400 to-emerald-600',
    },
    {
      metric: '100%',
      label: 'Confidential Process',
      description: 'Until you decide to announce',
      icon: '🔒',
      gradient: 'from-amber-400 to-amber-600',
    },
  ];

  const differentiators = [
    {
      title: 'Investment Banking Rigor',
      description:
        'We bring Wall Street discipline to middle market M&A, using institutional-grade processes that maximize value.',
      accent: 'from-blue-500 via-indigo-500 to-purple-500',
      hoverGlow: 'from-blue-500/10 via-indigo-500/10 to-purple-500/10',
      icon: BriefcaseIcon,
    },
    {
      title: 'True Auction Dynamics',
      description:
        'Unlike brokers who shop deals, we create genuine competitive tension among qualified buyers.',
      accent: 'from-purple-500 via-fuchsia-500 to-pink-500',
      hoverGlow: 'from-purple-500/10 via-fuchsia-500/10 to-pink-500/10',
      icon: LightningBoltIcon,
    },
    {
      title: 'Data-Driven Approach',
      description:
        'Every decision backed by market intelligence, comparable transactions, and buyer behavior analysis.',
      accent: 'from-emerald-400 via-teal-500 to-cyan-500',
      hoverGlow: 'from-emerald-400/10 via-teal-500/10 to-cyan-500/10',
      icon: ChartBarIcon,
    },
    {
      title: 'Aligned Incentives',
      description:
        'Our success fee structure ensures we only win when you achieve your optimal outcome.',
      accent: 'from-amber-400 via-orange-500 to-rose-500',
      hoverGlow: 'from-amber-400/10 via-orange-500/10 to-rose-500/10',
      icon: BadgeCheckIcon,
    },
  ];

  const confidentialityHighlights = [
    {
      title: 'Anonymous Marketing',
      description: 'Initial outreach uses code names. Identity revealed only after NDAs.',
      icon: EyeOffIcon,
      accent: 'from-blue-500 via-indigo-500 to-purple-500',
    },
    {
      title: 'Strict NDAs',
      description: 'Every buyer signs comprehensive confidentiality agreements before proceeding.',
      icon: ClipboardCheckIcon,
      accent: 'from-emerald-400 via-emerald-500 to-teal-500',
    },
    {
      title: 'Controlled Process',
      description: 'Information released strategically. Sensitive data only to serious buyers.',
      icon: AdjustmentsIcon,
      accent: 'from-amber-400 via-orange-500 to-rose-500',
    },
  ];

  return (
    <div className="bg-white overflow-hidden">
      {/* Hero Section - Clean & Engaging */}
      <div className="relative bg-gradient-to-b from-slate-50 to-white py-20 lg:py-28">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full blur-3xl opacity-30"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full blur-3xl opacity-30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
              The AMIVA Process
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 mb-6 tracking-tight">
              Investment Banking <span className="text-amiva-primary">Auction</span> Process
            </h1>

            <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
              A sophisticated 6-month journey that transforms your life's work into a competitive
              bidding war among qualified buyers
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/valuation"
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-br from-amiva-primary via-blue-600 to-blue-700 rounded-full shadow-lg hover:shadow-2xl overflow-hidden transition-all duration-300 hover:scale-105"
              >
                <span className="relative z-10">Start Your Auction Process</span>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              <a
                href={require('../../config/appConfig').appConfig.links.calendly}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-700 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-300 rounded-full shadow-md hover:shadow-lg hover:border-amiva-primary hover:bg-gradient-to-br hover:from-gray-50 hover:to-white hover:text-amiva-primary transition-all duration-300"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Section */}
      <div className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="metrics" className="observe-me grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {processMetrics.map((item, index) => (
              <div
                key={index}
                className={`group relative h-full ${
                  isVisible.metrics ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } transition-all duration-500`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div
                  className={`absolute inset-0 rounded-[28px] bg-gradient-to-br ${item.gradient} opacity-0 blur-2xl transition-all duration-500 group-hover:opacity-40`}
                ></div>

                <div className="relative h-full rounded-[26px] border border-white/60 bg-white/90 p-[1px] shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_26px_60px_rgba(15,23,42,0.14)]">
                  <div className="flex h-full flex-col rounded-[24px] bg-white/95 p-6">
                    <div className="mb-6 flex items-start justify-between">
                      <span
                        className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient} text-2xl shadow-lg shadow-slate-900/15 ring-4 ring-white/80`}
                      >
                        {item.icon}
                      </span>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-slate-400">
                        Metric
                      </span>
                    </div>

                    <div className="space-y-3">
                      <p className="text-[34px] font-bold leading-none text-slate-900">{item.metric}</p>
                      <p className="text-base font-semibold text-slate-700">{item.label}</p>
                    </div>

                    <div className="mt-auto pt-6">
                      <div className="mb-4 h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
                      <p className="text-sm leading-relaxed text-slate-500">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Interactive Auction Process Timeline */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              The Four Phases of <span className="text-amiva-primary">Maximum Value</span> Creation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each phase is meticulously designed to build momentum and competitive tension
              </p>
            </div>

          {/* Desktop Timeline */}
          <div className="hidden lg:block">
            <div className="relative pt-12">
              {/* Enhanced Progress Bar with Segments */}
              <div className="absolute top-0 left-0 right-0 h-2 flex items-center px-12" style={{ zIndex: 0 }}>
                <div className="flex-1 flex items-center gap-2">
                  {auctionPhases.map((phase, index) => (
                    <React.Fragment key={phase.id}>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${phase.color} transition-all duration-1000 ${
                            activePhase > index ? 'w-full' : activePhase === index ? 'w-1/2' : 'w-0'
                          }`}
                        ></div>
                      </div>
                      {index < auctionPhases.length - 1 && (
                        <ArrowRightIcon
                          className={`w-5 h-5 flex-shrink-0 transition-colors duration-500 ${
                            activePhase > index ? 'text-purple-600' : 'text-gray-300'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Phase Cards */}
              <div className="relative grid grid-cols-4 gap-6 pt-16">
                {auctionPhases.map((phase, index) => {
                  const PhaseIcon = phase.icon;
                  const isActive = activePhase === index;
                  const isCompleted = activePhase > index;
                  
                  return (
                    <div
                      key={phase.id}
                      className={`relative cursor-pointer transition-all duration-500 ${
                        isActive ? 'scale-105 z-10' : 'scale-100'
                      }`}
                      onClick={() => handlePhaseClick(index)}
                    >
                      {/* Enhanced Timeline Node */}
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-20">
                        <div className="relative">
                          {/* Glow effect for active phase */}
                          <div
                            className={`absolute inset-0 blur-xl transition-opacity duration-500 bg-gradient-to-br ${phase.glowColor} ${
                              isActive ? 'opacity-100' : 'opacity-0'
                            }`}
                          ></div>
                          
                          {/* Node container */}
                          <div
                            className={`relative w-16 h-16 rounded-2xl border-4 transition-all duration-500 ${
                              isActive
                                ? `${phase.borderColor} bg-white shadow-2xl ring-4 ${phase.ringColor}`
                                : isCompleted
                                ? `${phase.borderColor} bg-gradient-to-br ${phase.color}`
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            <div className="w-full h-full flex items-center justify-center">
                              {isCompleted && !isActive ? (
                                <CheckCircleIcon className="w-8 h-8 text-white" />
                              ) : (
                                <PhaseIcon
                                  className={`w-7 h-7 transition-all duration-500 ${
                                    isActive ? phase.textColor : 
                                    isCompleted ? 'text-white' : 'text-gray-400'
                                  } ${isActive ? 'animate-pulse' : ''}`}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Enhanced Phase Card */}
                      <div
                        className={`relative bg-white rounded-3xl p-6 pt-12 shadow-lg border-2 transition-all duration-500 overflow-hidden ${
                          isActive
                            ? `${phase.borderColor} shadow-2xl`
                            : isCompleted
                            ? 'border-gray-300'
                            : 'border-gray-200'
                        }`}
                      >
                        {/* Background gradient on active */}
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${phase.glowColor} transition-opacity duration-500 ${
                            isActive ? 'opacity-100' : 'opacity-0'
                          }`}
                        ></div>

                        {/* Card content */}
                        <div className="relative">
                          {/* Header */}
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <span
                                className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full transition-all duration-500 ${
                                  isActive
                                    ? `${phase.bgColor} ${phase.textColor}`
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                {phase.phase}
                              </span>
                              <span className={`text-xs font-medium ${isActive ? phase.textColor : 'text-gray-400'}`}>
                                {phase.weeks}
                              </span>
                            </div>
                            <h3 className={`text-lg font-bold mb-2 transition-colors duration-500 ${
                              isActive ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {phase.title}
                            </h3>
                          </div>

                          <p className={`text-sm leading-relaxed mb-4 transition-colors duration-500 ${
                            isActive ? 'text-gray-700' : 'text-gray-600'
                          }`}>
                            {phase.description}
                          </p>

                          {/* Activities (expandable) */}
                          <div
                            className={`space-y-2 mb-4 transition-all duration-500 ${
                              isActive ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                            }`}
                          >
                            {phase.activities.map((activity, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <CheckCircleIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${phase.textColor}`} />
                                <span className="text-xs text-gray-700 leading-relaxed">{activity}</span>
                              </div>
                            ))}
                          </div>

                          {/* Stats footer */}
                          <div className={`pt-4 border-t transition-colors duration-500 ${
                            isActive ? 'border-gray-200' : 'border-gray-100'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <div className={`text-3xl font-bold transition-all duration-500 bg-gradient-to-br ${phase.color} bg-clip-text text-transparent`}>
                                  {phase.stat}
                                </div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mt-1">
                                  {phase.statLabel}
                                </div>
                              </div>
                              <div className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all duration-500 ${
                                isActive ? `${phase.bgColor} ${phase.textColor}` : 'bg-gray-100 text-gray-600'
                              }`}>
                                {phase.outcome}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="lg:hidden space-y-6">
            {auctionPhases.map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isActive = activePhase === index;
              const isCompleted = activePhase > index;
              
              return (
                <div
                  key={phase.id}
                  className={`relative bg-white rounded-3xl p-6 shadow-lg border-2 transition-all duration-500 overflow-hidden ${
                    isActive
                      ? `${phase.borderColor} shadow-2xl`
                      : isCompleted
                      ? 'border-gray-300'
                      : 'border-gray-200'
                  }`}
                  onClick={() => handlePhaseClick(index)}
                >
                  {/* Background gradient on active */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${phase.glowColor} transition-opacity duration-500 ${
                      isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                  ></div>

                  {/* Card content */}
                  <div className="relative flex items-start gap-4">
                    {/* Icon */}
                    <div className="relative flex-shrink-0">
                      <div
                        className={`absolute inset-0 blur-xl transition-opacity duration-500 bg-gradient-to-br ${phase.glowColor} ${
                          isActive ? 'opacity-100' : 'opacity-0'
                        }`}
                      ></div>
                      <div
                        className={`relative w-14 h-14 rounded-2xl border-3 flex items-center justify-center transition-all duration-500 ${
                          isActive
                            ? `${phase.borderColor} bg-white shadow-lg ring-2 ${phase.ringColor}`
                            : isCompleted
                            ? `${phase.borderColor} bg-gradient-to-br ${phase.color}`
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isCompleted && !isActive ? (
                          <CheckCircleIcon className="w-7 h-7 text-white" />
                        ) : (
                          <PhaseIcon
                            className={`w-6 h-6 transition-all duration-500 ${
                              isActive
                                ? phase.textColor
                                : isCompleted
                                ? 'text-white'
                                : 'text-gray-400'
                            }`}
                          />
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full transition-all duration-500 ${
                            isActive
                              ? `${phase.bgColor} ${phase.textColor}`
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {phase.phase}
                        </span>
                        <span className={`text-xs font-medium ${isActive ? phase.textColor : 'text-gray-400'}`}>
                          {phase.weeks}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">{phase.description}</p>

                      {/* Activities (expandable on active) */}
                      {isActive && (
                        <div className="space-y-2 mb-4">
                          {phase.activities.map((activity, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <CheckCircleIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${phase.textColor}`} />
                              <span className="text-xs text-gray-700 leading-relaxed">{activity}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Stats footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div>
                          <div className={`text-3xl font-bold bg-gradient-to-br ${phase.color} bg-clip-text text-transparent`}>
                            {phase.stat}
                          </div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mt-1">
                            {phase.statLabel}
                          </div>
                        </div>
                        <div className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all duration-500 ${
                          isActive ? `${phase.bgColor} ${phase.textColor}` : 'bg-gray-100 text-gray-600'
                        }`}>
                          {phase.outcome}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Why This Works Section */}
      <div className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Investment Banking <span className="text-amiva-primary">Auction Dynamics</span> Work
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The same process that sells billion-dollar companies, adapted for the middle market
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {differentiators.map((item, index) => {
              const DifferentiatorIcon = item.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-500"
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${item.hoverGlow} opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300`}
                  ></div>

                  <div className="relative">
                    <div className="flex items-start gap-4">
                      <div className="relative flex-shrink-0">
                        <div
                          className={`absolute inset-0 blur-xl opacity-60 transition-opacity duration-500 bg-gradient-to-br ${item.accent} group-hover:opacity-90`}
                        ></div>
                        <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-lg shadow-gray-200/70 ring-1 ring-white/60">
                          <div
                            className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${item.accent} text-white transition-transform duration-500 group-hover:scale-105`}
                          >
                            <DifferentiatorIcon className="h-6 w-6" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Competitive Advantage Visual */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                The Power of <span className="text-amiva-primary">Competitive</span> Tension
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                When buyers know they're competing, everything changes. Valuations increase,
                terms improve, and closing becomes certain.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Broad Market Reach</h3>
                    <p className="text-gray-600">
                      We have access to nearly 200,000 qualified buyers, ensuring no stone is left unturned in
                      finding your perfect acquirer.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Structured Bidding</h3>
                    <p className="text-gray-600">
                      Multiple rounds of bidding create urgency and FOMO, driving buyers to put
                      their best foot forward.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Optimal Outcomes</h3>
                    <p className="text-gray-600">
                      You choose from multiple offers, selecting not just the highest price but
                      the best overall fit.
                    </p>
                  </div>
                </div>
              </div>
            </div>

                        <div className="relative">
              {/* Premium Auction Visualization */}
              <div className="relative mx-auto w-full max-w-2xl">
                {/* Ambient Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-3xl blur-3xl"></div>
                
                {/* Main Container with Glass Effect */}
                <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-100/50 overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-gray-50 to-white px-8 py-6 border-b border-gray-100">
                    <h3 className="text-2xl font-bold text-gray-900 text-center">The Auction Dynamic</h3>
                    <p className="text-gray-600 text-center mt-2">Watch how competition drives value</p>
                  </div>

                  {/* Visualization Area */}
                  <div className="relative p-8 lg:p-12">
                    <div className="relative h-96 flex items-center justify-center">
                      {/* Concentric Guide Circles */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="absolute w-80 h-80 rounded-full border border-gray-100/40"></div>
                        <div className="absolute w-64 h-64 rounded-full border border-gray-100/30"></div>
                        <div className="absolute w-48 h-48 rounded-full border border-gray-100/20"></div>
                      </div>

                      {/* Energy Lines Radiating from Center */}
                      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ animation: 'rotate 60s linear infinite' }}>
                        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                          <line
                            key={angle}
                            x1="50%"
                            y1="50%"
                            x2={`${50 + 45 * Math.cos((angle * Math.PI) / 180)}%`}
                            y2={`${50 + 45 * Math.sin((angle * Math.PI) / 180)}%`}
                            stroke="url(#gradient-line)"
                            strokeWidth="0.5"
                            opacity="0.2"
                          />
                        ))}
                        <defs>
                          <linearGradient id="gradient-line" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
                            <stop offset="50%" stopColor="#a855f7" stopOpacity="0.5" />
                            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                      </svg>

                      {/* Central Business Hub - Premium Design */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                        <div className="relative group">
                          {/* Glow Effect */}
                          <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                          
                          {/* Main Hub */}
                          <div className="relative w-40 h-40 bg-gradient-to-br from-white to-gray-50 rounded-full shadow-2xl border-2 border-gray-100 flex items-center justify-center transform group-hover:scale-105 transition-all duration-500">
                            {/* Inner Circle */}
                            <div className="absolute inset-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full opacity-5"></div>
                            
                            {/* Content */}
                            <div className="relative text-center">
                              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mx-auto mb-2 flex items-center justify-center shadow-lg transform group-hover:rotate-3 transition-transform duration-500">
                                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                                </svg>
                              </div>
                              <div className="text-sm font-bold text-gray-800 uppercase tracking-wider">Your</div>
                              <div className="text-base font-bold text-amiva-primary">BUSINESS</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Orbiting Buyers - Premium Cards */}
                      {[
                        { icon: '🏛️', label: 'Strategic Buyer', desc: '$10M+', color: 'from-blue-500 to-blue-600', startAngle: 0 },
                        { icon: '💎', label: 'PE Fund', desc: '$15M+', color: 'from-purple-500 to-purple-600', startAngle: 72 },
                        { icon: '🚀', label: 'Competitor', desc: '$12M+', color: 'from-emerald-500 to-emerald-600', startAngle: 144 },
                        { icon: '🌐', label: 'Global Corp', desc: '$18M+', color: 'from-amber-500 to-amber-600', startAngle: 216 },
                        { icon: '⚡', label: 'Synergy Play', desc: '$14M+', color: 'from-pink-500 to-pink-600', startAngle: 288 },
                      ].map((buyer, i) => (
                        <div
                          key={i}
                          className="absolute"
                          style={{
                            top: '50%',
                            left: '50%',
                            transform: `translate(-50%, -50%) rotate(${buyer.startAngle}deg) translateX(175px) rotate(-${buyer.startAngle}deg)`,
                            animation: `orbit-premium-${i} 20s linear infinite`,
                          }}
                        >
                          <div className="relative group cursor-pointer">
                            {/* Card - Slightly Smaller */}
                            <div className={`relative bg-white rounded-xl shadow-md border border-gray-200 p-2 transform transition-all duration-300 hover:scale-110 hover:shadow-xl hover:z-10`}>
                              {/* Gradient Border on Hover */}
                              <div className={`absolute inset-0 bg-gradient-to-br ${buyer.color} rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                              <div className="relative bg-white rounded-lg p-1.5">
                                <div className="flex items-center gap-1.5">
                                  <div className={`w-8 h-8 bg-gradient-to-br ${buyer.color} rounded-md flex items-center justify-center shadow-sm flex-shrink-0`}>
                                    <span className="text-sm">{buyer.icon}</span>
                                  </div>
                                  <div>
                                    <div className="text-[11px] font-semibold text-gray-800 whitespace-nowrap">{buyer.label}</div>
                                    <div className="text-[9px] text-gray-500">{buyer.desc}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Connection Line to Center */}
                            <div className="absolute top-1/2 left-1/2 w-40 h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent opacity-0 group-hover:opacity-30 transition-opacity duration-500 origin-center"
                                 style={{ transform: `translateX(-50%) translateY(-50%) rotate(${-buyer.startAngle}deg) translateX(-70px)` }}></div>
                          </div>
                        </div>
                      ))}

                      {/* Animated Pulse Rings */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="absolute w-60 h-60 rounded-full border-2 border-blue-400/20 animate-ping"></div>
                        <div className="absolute w-52 h-52 rounded-full border-2 border-purple-400/20 animate-ping" style={{ animationDelay: '0.5s' }}></div>
                        <div className="absolute w-44 h-44 rounded-full border-2 border-pink-400/20 animate-ping" style={{ animationDelay: '1s' }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Stats Bar */}
                  <div className="bg-gradient-to-r from-gray-50 via-white to-gray-50 px-8 py-6 border-t border-gray-100">
                    <div className="flex items-center justify-center gap-8 lg:gap-16">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <span className="text-3xl font-bold text-gray-900">3.5</span>
                          <span className="text-xl text-gray-400">avg</span>
                        </div>
                        <p className="text-sm text-gray-600">Competing Offers</p>
                      </div>
                      
                      <div className="h-12 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                      
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <span className="text-3xl font-bold text-gray-900">35%</span>
                          <span className="text-xl text-gray-400">↑</span>
                        </div>
                        <p className="text-sm text-gray-600">Value Increase</p>
                      </div>
                      
                      <div className="h-12 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                      
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <span className="text-3xl font-bold text-gray-900">95%</span>
                          <span className="text-xl text-gray-400">✓</span>
                        </div>
                        <p className="text-sm text-gray-600">Close Rate</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confidentiality Assurance */}
      <div className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-12 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
            </div>

            <div className="relative">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur rounded-2xl mb-6">
                  <span className="text-3xl">🔒</span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                  Complete Confidentiality
            </h2>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                  Your employees, customers, and competitors won't know until you're ready
            </p>
          </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {confidentialityHighlights.map((item) => {
                  const HighlightIcon = item.icon;
                  return (
                    <div key={item.title} className="text-center">
                      <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-all duration-500 hover:-translate-y-1 hover:border-white/20 hover:bg-white/10">
                        <div className="mb-6 flex justify-center">
                          <div className="relative">
                            <div className={`absolute inset-0 blur-2xl opacity-40 bg-gradient-to-br ${item.accent}`}></div>
                            <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/10 backdrop-blur">
                              <div className={`flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${item.accent} text-white shadow-lg transition-transform duration-500 group-hover:scale-105`}>
                                <HighlightIcon className="h-7 w-7" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-300">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Ready to Maximize Your <span className="text-amiva-primary">Exit Value</span>?
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Let's discuss how our investment banking auction process can create competitive
            dynamics for your business
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
            <Link
              to="/valuation"
              className="group relative inline-flex items-center justify-center px-10 py-5 text-lg font-semibold text-white bg-gradient-to-br from-amiva-primary via-blue-600 to-blue-700 rounded-full shadow-lg hover:shadow-2xl overflow-hidden transition-all duration-300 hover:scale-105"
            >
              <span className="relative z-10">Get Your Business Valuation</span>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>
            
            <a
              href={require('../../config/appConfig').appConfig.links.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-5 text-lg font-semibold text-gray-700 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-300 rounded-full shadow-md hover:shadow-lg hover:border-amiva-primary hover:bg-gradient-to-br hover:from-gray-50 hover:to-white hover:text-amiva-primary transition-all duration-300"
            >
              Schedule a Confidential Call
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>100% Confidential</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>No Obligation</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Institutional Process</span>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
}
